/**
 * DriveZone — ConfirmDistance Panel
 * Right panel of the split-screen layout.
 * Enter two US cities → get exact driving time, distance, and a map preview.
 */

import { useState } from 'react'
import { ArrowRight, Clock, Route, Loader2, Navigation } from 'lucide-react'
import api from '@/lib/api'
import { useSearchStore } from '@/stores/searchStore'
import LocationInput from '@/components/LocationInput'
import type { ConfirmResult } from '@/stores/searchStore'
import { AxiosError } from 'axios'

export default function ConfirmDistance() {
  const {
    confirmResult,    setConfirmResult,
    confirmLoading,   setConfirmLoading,
    confirmError,     setConfirmError,
    setShowQuotaModal,
  } = useSearchStore()

  const [origin, setOrigin]           = useState('')
  const [destination, setDestination] = useState('')

  async function handleConfirm() {
    if (!origin.trim() || !destination.trim()) {
      setConfirmError('Please enter both locations.')
      return
    }

    setConfirmError('')
    setConfirmLoading(true)
    setConfirmResult(null)

    try {
      const res = await api.post<ConfirmResult>('/api/search/confirm/', {
        origin: origin.trim(),
        destination: destination.trim(),
      })
      setConfirmResult(res.data)
      const from = res.data.origin.name.split(',')[0]
      const to   = res.data.destination.name.split(',')[0]
      document.title = `${from} to ${to}: ${res.data.drive_minutes} min drive | DriveZone`
    } catch (err) {
      const axiosErr = err as AxiosError<{ error?: string; message?: string }>
      if (axiosErr.response?.status === 402) {
        setShowQuotaModal(true)
      } else if (axiosErr.response?.status === 503) {
        setConfirmError('Google Maps is temporarily unavailable. Please try again in a moment.')
      } else {
        setConfirmError(
          axiosErr.response?.data?.message ?? 'Could not calculate distance. Please try again.'
        )
      }
    } finally {
      setConfirmLoading(false)
    }
  }

  const hours    = confirmResult ? Math.floor(confirmResult.drive_minutes / 60) : 0
  const minutes  = confirmResult ? confirmResult.drive_minutes % 60 : 0
  const timeStr  = hours > 0 ? `${hours}h ${minutes}m` : `${minutes} min`

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="flex-shrink-0 pb-4 border-b border-slate-100">
        <h2 className="text-base font-semibold text-slate-900">Confirm Distance</h2>
        <p className="text-xs text-slate-500 mt-0.5">
          Check the exact driving time between any two US locations
        </p>
      </div>

      {/* Controls */}
      <div className="flex-shrink-0 space-y-3 pt-4">
        <LocationInput
          placeholder="From — e.g. Trenton, TX"
          onSelect={setOrigin}
          value={origin}
          onChange={setOrigin}
        />

        <div className="flex items-center gap-2">
          <div className="flex-1 h-px bg-slate-200" />
          <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        <LocationInput
          placeholder="To — e.g. McKinney, TX"
          onSelect={setDestination}
          value={destination}
          onChange={setDestination}
        />

        <button
          onClick={handleConfirm}
          disabled={confirmLoading || !origin.trim() || !destination.trim()}
          className="btn-primary w-full"
        >
          {confirmLoading
            ? <><Loader2 className="w-4 h-4 animate-spin" /> Calculating…</>
            : <><Navigation className="w-4 h-4" /> Confirm Distance</>
          }
        </button>

        {confirmError && (
          <p className="text-sm text-red-500 text-center">{confirmError}</p>
        )}
      </div>

      {/* Result */}
      <div className="flex-1 overflow-y-auto min-h-0 pt-4">
        {confirmLoading && (
          <div className="card p-5 space-y-4 animate-pulse">
            <div className="h-5 bg-slate-200 rounded w-3/4" />
            <div className="h-24 bg-slate-200 rounded-xl" />
            <div className="h-5 bg-slate-200 rounded w-1/2 mx-auto" />
          </div>
        )}

        {!confirmLoading && confirmResult && (
          <div className="card p-5 space-y-4">
            {/* Route header */}
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-800 truncate text-sm">
                {confirmResult.origin.name.split(',').slice(0, 2).join(',')}
              </span>
              <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span className="font-semibold text-slate-800 truncate text-sm">
                {confirmResult.destination.name.split(',').slice(0, 2).join(',')}
              </span>
              {confirmResult.cached && (
                <span className="ml-auto text-xs text-emerald-600 font-medium flex-shrink-0">⚡</span>
              )}
            </div>

            {/* Drive time — large */}
            <div className="bg-primary-50 rounded-xl py-5 text-center">
              <div className="text-5xl font-bold text-primary-700 tracking-tight">
                {timeStr}
              </div>
              <div className="text-xs text-primary-400 mt-1 font-medium uppercase tracking-wide">
                driving time
              </div>
            </div>

            {/* Stats row */}
            <div className="flex items-center justify-center gap-6">
              <div className="flex items-center gap-1.5 text-sm text-slate-600">
                <Route className="w-4 h-4 text-slate-400" />
                <span className="font-medium">{confirmResult.drive_km} km</span>
              </div>
              <div className="w-px h-4 bg-slate-200" />
              <div className="flex items-center gap-1.5 text-sm text-slate-600">
                <Clock className="w-4 h-4 text-slate-400" />
                <span className="font-medium">{confirmResult.drive_miles} mi</span>
              </div>
            </div>
          </div>
        )}

        {!confirmLoading && !confirmResult && !confirmError && (
          <div className="flex flex-col items-center justify-center h-full
                          text-center text-slate-400 py-12 gap-2">
            <Navigation className="w-8 h-8 opacity-30" />
            <p className="text-sm">Enter two locations to confirm the driving distance</p>
          </div>
        )}
      </div>
    </div>
  )
}
